import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { AuthContext } from '../context/AuthContext';
import { BellIcon, CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';

const NotificationDropdown = () => {
    const { token } = useContext(AuthContext);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Bildirimleri yükle
    const fetchNotifications = useCallback(async () => {
        if (!token) return;
        
        try {
            setLoading(true);
            const config = { headers: { 'x-auth-token': token } };
            const res = await axios.get(`${API_BASE_URL}/api/notifications`, config);
            setNotifications(res.data.notifications);
            setUnreadCount(res.data.unreadCount);
        } catch (err) {
            console.error('Bildirimler yüklenemedi:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // İlk yükleme ve her 30 saniyede bir güncelle
    useEffect(() => {
        if (token) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [token, fetchNotifications]);

    // Dropdown dışına tıklanınca kapat
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Bildirimi okundu olarak işaretle
    const markAsRead = async (notificationId, link) => {
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.put(`${API_BASE_URL}/api/notifications/${notificationId}/read`, {}, config);
            
            // State'i güncelle
            setNotifications(prev => 
                prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
            
            // Linke yönlendir
            if (link) {
                window.location.href = link;
            }
        } catch (err) {
            console.error('Notification could not be updated:', err);
        }
    };

    // Bildirimi sil
    const deleteNotification = async (notificationId) => {
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.delete(`${API_BASE_URL}/api/notifications/${notificationId}`, config);
            
            setNotifications(prev => prev.filter(n => n._id !== notificationId));
            if (!notifications.find(n => n._id === notificationId)?.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Notification could not be deleted:', err);
        }
    };

    // Tümünü okundu işaretle
    const markAllAsRead = async () => {
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.put(`${API_BASE_URL}/api/notifications/mark-all-read`, {}, config);
            
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Operation failed:', err);
        }
    };

    // Tümünü sil
    const deleteAll = async () => {
        try {
            const config = { headers: { 'x-auth-token': token } };
            await axios.delete(`${API_BASE_URL}/api/notifications`, config);
            
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error('Operation failed:', err);
        }
    };

    if (!token) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Notification Icon */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-light-text dark:text-dark-text hover:bg-light-card dark:hover:bg-dark-card rounded-lg transition-colors"
            >
                {unreadCount > 0 ? (
                    <BellSolidIcon className="h-6 w-6 text-primary-500" />
                ) : (
                    <BellIcon className="h-6 w-6" />
                )}

                {/* Unread count badge */}
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] sm:max-h-96 overflow-y-auto bg-white dark:bg-dark-card rounded-lg shadow-xl border border-light-border dark:border-dark-border z-50"
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-white dark:bg-dark-card border-b border-light-border dark:border-dark-border p-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold text-light-text dark:text-dark-text">
                                    Notifications
                                </h3>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>
                            
                                            {notifications.length > 0 && (
                                <div className="flex gap-2 text-xs sm:text-sm">
                                    <button
                                        onClick={markAllAsRead}
                                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                    >
                                        Mark All as Read
                                    </button>
                                    <span className="text-gray-400">•</span>
                                    <button
                                        onClick={deleteAll}
                                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                        Delete All
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Bildirim Listesi */}
                        {loading ? (
                            <div className="p-8 text-center text-gray-500">
                                Loading...
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                <BellIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                                <p>You don't have a notification yet</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-light-border dark:divide-dark-border">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification._id}
                                        className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                                            !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            {/* İkon */}
                                            <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                                                !notification.isRead ? 'bg-blue-500' : 'bg-gray-300'
                                            }`} />
                                            
                                            {/* İçerik */}
                                            <div className="flex-1 min-w-0">
                                                <button
                                                    onClick={() => markAsRead(notification._id, notification.link)}
                                                    className="text-left w-full"
                                                >
                                                    <h4 className="text-sm font-semibold text-light-text dark:text-dark-text mb-1 break-words">
                                                        {notification.title}
                                                    </h4>
                                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 break-words">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                        {new Date(notification.createdAt).toLocaleDateString('tr-TR', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                </button>
                                            </div>
                                            
                                            {/* Aksiyonlar */}
                                            <div className="flex flex-col sm:flex-row items-center gap-1">
                                                {!notification.isRead && (
                                                    <button
                                                        onClick={() => markAsRead(notification._id, null)}
                                                        className="p-1.5 sm:p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                                                        title="Okundu işaretle"
                                                    >
                                                        <CheckIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => deleteNotification(notification._id)}
                                                    className="p-1.5 sm:p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                                    title="Sil"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationDropdown;
